import axios from 'axios'
import BaseService from '@src/services/BaseService'
import * as storeFile from '@state/store'
import QuestionnairesService from '@src/services/QuestionnairesService'
import QuestionnaireItemsService from '@src/services/QuestionnaireItemsService'
import { extractQuestionMetaFromItem } from '@src/utils/lojaPerfeitaQuestionRuleUi'

const RULE_TYPES = {
    binary: 'binary',
    binary_all: 'binary_all',
    proportional: 'proportional',
    dependency: 'dependency',
    comparative: 'comparative',
}

const WHEN_DEPENDENCY_MISSING = {
    exclude: 'exclude',
    zero: 'zero',
}

function tenantPath(suffix) {
    const tid = storeFile.default.getters['auth/tenantId']
    return `tenant/${tid}/${suffix}`
}

/** API macro: { success, data, message } — inner `data` is the payload. */
function unwrapInner(axiosRes) {
    const inner = axiosRes.data && Object.prototype.hasOwnProperty.call(axiosRes.data, 'data')
        ? axiosRes.data.data
        : axiosRes.data
    return { data: inner }
}

function mapPerformanceBand(b) {
    if (!b || typeof b !== 'object') {
        return b
    }
    return {
        ...b,
        min_score: b.min_percent != null ? Number(b.min_percent) : b.min_score,
        max_score: b.max_percent != null ? Number(b.max_percent) : b.max_score,
    }
}

function listFromMacroResponse(res) {
    const inner = unwrapInner(res).data
    return (inner && inner.data) || []
}

class LojaPerfeitaService extends BaseService {
    constructor() {
        super('loja-perfeita/campaigns')
    }

    _post(path, body = {}) {
        return axios.post(tenantPath(path), body)
    }

    static get RULE_TYPES() {
        return RULE_TYPES
    }

    static get WHEN_DEPENDENCY_MISSING() {
        return WHEN_DEPENDENCY_MISSING
    }

    fetchVuetable(apiUrl, { params } = { params: {} }) {
        const parameters = this.getSorting(params)
        return this.fetchAll(parameters, apiUrl).then((res) => ({
            data: res.data.data,
        }))
    }

    getById(id) {
        return this.fetchById(id, {}).then((res) => unwrapInner(res))
    }

    create(params) {
        return super.create(params).then((res) => unwrapInner(res))
    }

    update(id, params) {
        return super.update(id, params).then((res) => unwrapInner(res))
    }

    delete(id, params = {}) {
        return super.delete(id, params).then((res) => unwrapInner(res))
    }

    getPillars(campaignId) {
        return this._post(`loja-perfeita/campaigns/${campaignId}/pillars`, {}).then((res) => ({
            data: listFromMacroResponse(res),
        }))
    }

    createPillar(campaignId, params) {
        return this._post(`loja-perfeita/campaigns/${campaignId}/pillars/create`, params).then((res) => unwrapInner(res))
    }

    updatePillar(id, params) {
        return this._post(`loja-perfeita/pillars/${id}/update`, params).then((res) => unwrapInner(res))
    }

    deletePillar(id) {
        return this._post(`loja-perfeita/pillars/${id}/delete`, {}).then((res) => unwrapInner(res))
    }

    getCriteria(campaignId) {
        return this._post(`loja-perfeita/campaigns/${campaignId}/criteria`, {}).then((res) => ({
            data: listFromMacroResponse(res),
        }))
    }

    createCriterion(pillarId, params) {
        return this._post(`loja-perfeita/pillars/${pillarId}/criteria/create`, params).then((res) => unwrapInner(res))
    }

    updateCriterion(id, params) {
        return this._post(`loja-perfeita/criteria/${id}/update`, params).then((res) => unwrapInner(res))
    }

    deleteCriterion(id) {
        return this._post(`loja-perfeita/criteria/${id}/delete`, {}).then((res) => unwrapInner(res))
    }

    getCriterionQuestions(criterionId) {
        return this._post(`loja-perfeita/criteria/${criterionId}/tasks`, {}).then((res) => ({
            data: listFromMacroResponse(res),
        }))
    }

    createCriterionQuestion(criterionId, params) {
        return this._post(`loja-perfeita/criteria/${criterionId}/tasks/create`, params).then((res) => unwrapInner(res))
    }

    updateCriterionQuestion(id, params) {
        return this._post(`loja-perfeita/criterion-tasks/${id}/update`, params).then((res) => unwrapInner(res))
    }

    deleteCriterionQuestion(id) {
        return this._post(`loja-perfeita/criterion-tasks/${id}/delete`, {}).then((res) => unwrapInner(res))
    }

    getPerformanceBands(campaignId) {
        return this._post(`loja-perfeita/campaigns/${campaignId}/performance-bands`, {}).then((res) => ({
            data: listFromMacroResponse(res).map(mapPerformanceBand),
        }))
    }

    createPerformanceBand(campaignId, params) {
        const body = {
            label: params.label,
            min_percent: params.min_score != null ? Number(params.min_score) : 0,
            max_percent: params.max_score != null ? Number(params.max_score) : 100,
            color: params.color,
        }
        return this._post(`loja-perfeita/campaigns/${campaignId}/performance-bands/create`, body).then((res) => ({
            data: mapPerformanceBand(unwrapInner(res).data),
        }))
    }

    updatePerformanceBand(id, params) {
        const body = {}
        if (params.label !== undefined) body.label = params.label
        if (params.color !== undefined) body.color = params.color
        if (params.min_score !== undefined) body.min_percent = params.min_score
        if (params.max_score !== undefined) body.max_percent = params.max_score
        return this._post(`loja-perfeita/performance-bands/${id}/update`, body).then((res) => ({
            data: mapPerformanceBand(unwrapInner(res).data),
        }))
    }

    deletePerformanceBand(id) {
        return this._post(`loja-perfeita/performance-bands/${id}/delete`, {}).then((res) => unwrapInner(res))
    }

    // ── Conditions (Segmentation) ────────────────────────────────────

    getCampaignConditions(campaignId) {
        return this._post(`loja-perfeita/campaigns/${campaignId}/conditions`, {}).then((res) => ({
            data: listFromMacroResponse(res),
        }))
    }

    createCampaignCondition(campaignId, params) {
        return this._post(`loja-perfeita/campaigns/${campaignId}/conditions/create`, params).then((res) => unwrapInner(res))
    }

    getPillarConditions(pillarId) {
        return this._post(`loja-perfeita/pillars/${pillarId}/conditions`, {}).then((res) => ({
            data: listFromMacroResponse(res),
        }))
    }

    createPillarCondition(pillarId, params) {
        return this._post(`loja-perfeita/pillars/${pillarId}/conditions/create`, params).then((res) => unwrapInner(res))
    }

    getCriterionConditions(criterionId) {
        return this._post(`loja-perfeita/criteria/${criterionId}/conditions`, {}).then((res) => ({
            data: listFromMacroResponse(res),
        }))
    }

    createCriterionCondition(criterionId, params) {
        return this._post(`loja-perfeita/criteria/${criterionId}/conditions/create`, params).then((res) => unwrapInner(res))
    }

    updateCondition(conditionId, params) {
        return this._post(`loja-perfeita/conditions/${conditionId}/update`, params).then((res) => unwrapInner(res))
    }

    deleteCondition(conditionId) {
        return this._post(`loja-perfeita/conditions/${conditionId}/delete`, {}).then((res) => unwrapInner(res))
    }

    // ── Questionnaires ────────────────────────────────────────────────

    getQuestionnaires() {
        return QuestionnairesService.fetchAll({ page: 1, per_page: 999 })
            .then((res) => {
                const raw = (res.data && res.data.data) || res.data || []
                const list = Array.isArray(raw) ? raw : []
                return { data: list }
            })
            .catch(() => ({ data: [] }))
    }

    getQuestionnaireTasks(questionnaireId) {
        if (!questionnaireId) return Promise.resolve({ data: [] })
        return Promise.all([
            QuestionnairesService.fetchById(questionnaireId, {}).catch(() => ({ data: { type: 'generic' } })),
            QuestionnaireItemsService.fetchAll({
                search: `questionnaire_id:${questionnaireId}`,
                with: ['question', 'choices'],
                orderBy: 'position',
                per_page: 999,
            }).catch(() => ({ data: { data: [] } })),
        ]).then(([questionnaireRes, itemsRes]) => {
            const questionnaire = questionnaireRes.data && (questionnaireRes.data.data || questionnaireRes.data)
            const questionnaireType = (questionnaire && questionnaire.type) || 'generic'
            const rawItems = (itemsRes.data && itemsRes.data.data) || itemsRes.data || []
            const list = (Array.isArray(rawItems) ? rawItems : []).map((item) => {
                const meta = extractQuestionMetaFromItem(item)
                return {
                    id: item.id,
                    label: item.label || (item.question && item.question.question) || `#${item.id}`,
                    questionnaire_id: Number(questionnaireId),
                    questionnaire_type: questionnaireType,
                    ...meta,
                }
            })
            return { data: list }
        })
    }
}

export default new LojaPerfeitaService()
export { LojaPerfeitaService as LojaPerfeitaServiceClass }
